import { Theme, ThemeMode, ThemeName } from 'foundations-theming-base/types';
import CanvasLightTheme from 'foundations-theming-base/stable/canvas/light/theme';
import TrellisLightTheme from 'foundations-theming-base/stable/trellis/light/theme';
import { MODERN_THEMES } from 'foundations-theming-base/modern-themes';
import { getDeployedTheme } from '../../utils/getDeployedTheme';
import type { ThemeSettings } from '../../utils/getLocalThemeSettings';
import { Metrics } from '../../utils/metrics';
import { getWindow } from '../../utils/utils';
import devLogger from 'react-utils/devLogger';
import { applyThemeOverrides } from '../../utils/dev-overrides';

// these are used in foundations-components directly, so we need to keep them here for now
export const FLASH_TOKENS_STORAGE_KEY = 'trellis:flash-unsafe-tokens';
export const FLASH_CSS_VAR = '--trellis-flash-token';

export function trackTheme(themeName: ThemeName, themeMode: ThemeMode) {
  try {
    Metrics.counter('theme', {
      themeName,
      themeMode,
    }).increment();
  } catch (e) {
    // do nothing
  }
}

export const getDefaultTheme = (): Theme => {
  return CanvasLightTheme;
};

function getBuiltinTheme(themeName: ThemeName): Theme {
  if (themeName === 'trellis') {
    return TrellisLightTheme;
  }
  const isModernTheme = Object.values(MODERN_THEMES).some(
    (modernTheme) => modernTheme.themeName === themeName
  );
  return isModernTheme ? TrellisLightTheme : CanvasLightTheme;
}

function getWindowTheme(themeSettings: ThemeSettings): Theme | undefined {
  const window = getWindow();
  const accessor = `${themeSettings.themeName}-${themeSettings.themeMode}`;

  if (window.__hubspotTheme && window.__hubspotTheme[accessor]) {
    return window.__hubspotTheme[accessor];
  }

  Metrics.counter('missing-window-theme', {
    themeName: themeSettings.themeName,
    themeMode: themeSettings.themeMode,
    windowName: window.name ?? '',
    windowPathname: window.location?.pathname ?? '',
  }).increment();

  return undefined;
}

export function isSyncThemeSettings(themeSettings: ThemeSettings) {
  const isSyncVersion = themeSettings.themeVersion.type === 'default';

  // themes we do not code-split
  const isSyncTheme =
    themeSettings.themeMode === 'light' &&
    (themeSettings.themeName === 'canvas' ||
      themeSettings.themeName === 'trellis');

  return isSyncVersion && isSyncTheme;
}

/**
 * We never want to block rendering on loading the theme, so we always initially try to get the theme synchronously
 * and then load it asynchronously if the settings indicate we should.
 */
function getThemeSync(themeSettings: ThemeSettings): Theme {
  let theme = getBuiltinTheme(themeSettings.themeName);

  const maybeWindowTheme = getWindowTheme(themeSettings);
  if (maybeWindowTheme) {
    theme = maybeWindowTheme;
  }

  return theme;
}

export function getThemeToProvideSync(themeSettings: ThemeSettings): Theme {
  const theme = getThemeSync(themeSettings);
  return applyThemeOverrides(theme, themeSettings.themeOverrides);
}

export async function getThemeToProvide(themeSettings: ThemeSettings) {
  const { themeVersion, themeOverrides } = themeSettings;

  let theme: Theme | undefined = undefined;

  if (themeVersion.type === 'default') {
    theme = getThemeSync(themeSettings);
  } else {
    theme = await getDeployedTheme(themeSettings);
  }

  if (!theme) {
    devLogger.warn({
      message: `Theme not found for settings: ${JSON.stringify(
        themeSettings
      )}. Falling back to default theme.`,
      key: 'theme-provider:theme-not-found',
    });
  }

  const finalTheme = theme ?? getDefaultTheme();
  return applyThemeOverrides(finalTheme, themeOverrides);
}
